import React, { useState } from 'react';
import { AsyncPaginate } from 'react-select-async-paginate';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useApiClient from '../../../hooks/api/useApiClient';
import usePermissions, { ManagerRole } from '../../../hooks/auth/usePermissions';
import useTheme from '../../../hooks/context/useTheme';
import TestRestaurantTag from '../../provider-billing/TestRestaurantTag';

export default function CompanySelect({
  value,
  except = [],
  onChange,
  id = undefined,
  isMulti = false,
  showAddress = false,
  className,
  franchiseId = null,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Form:Business:CompanySelect' });

  const { franchise } = useSelector((state) => state.currentFranchise);
  const designationVersion = useSelector((state) => state.testRestaurants?.version ?? 0);

  const { CompaniesManager } = useApiClient();
  const { hasAnyRole } = usePermissions();
  const theme = useTheme();

  const isMerchant = hasAnyRole([ManagerRole.StoreAdmin, ManagerRole.StoreEmployee, ManagerRole.StoreOperator], false);

  const [, setSearch] = useState('');

  const loadData = async (search, loadedOptions, { page }) => {
    let hasMore = false;

    const options = await CompaniesManager.get({
      name: search,
      franchise_id: franchiseId ?? (isMerchant ? franchise?.id : null),
      except,
      page,
    }).then((response) => {
      const { data } = response;

      hasMore = data.current_page < data.last_page;

      return data.data;
    });

    return {
      options,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const getOptionLabel = (option) => {
    if (!showAddress || !option.address) {
      return option.name;
    }

    return (
      <div>
        <strong>{option.name}</strong>
        <p className="tiny text-gray">
          {option.address}
          {', '}
          {option.city} {option.zip_code}
        </p>
      </div>
    );
  };

  return (
    <AsyncPaginate
      id={id}
      className={classnames('cursor-pointer border-none', className)}
      loadOptions={loadData}
      onChange={(item) => onChange(item)}
      onInputChange={setSearch}
      debounceTimeout={500}
      isClearable
      value={value}
      // By id, not by object: a prefilled value (e.g. an API account's own companies) is never
      // the same object as the loaded option, so identity let a restaurant be picked twice.
      getOptionValue={(option) => String(option.id)}
      getOptionLabel={getOptionLabel}
      // Reload the cached options once any restaurant is marked or unmarked
      // as the test restaurant, so the TEST tags in the list follow — from
      // whichever screen it was done on. Only that: keying on the selected id
      // would drop every picker's cache on every restaurant switch.
      cacheUniqs={[designationVersion]}
      // Menu rows only: the selected value stays the plain label, so the
      // sidebar header's own tag is not doubled and a multi-select chip's
      // "Remove …" label keeps the restaurant's name. getOptionLabel above
      // still feeds filtering and a11y text.
      formatOptionLabel={(option, { context }) =>
        context === 'menu' && option?.is_test_restaurant ? (
          <span className="inline-flex items-center gap-2">
            {getOptionLabel(option)}
            <TestRestaurantTag company={option} />
          </span>
        ) : (
          getOptionLabel(option)
        )
      }
      isMulti={isMulti}
      placeholder={t('placeholder')}
      styles={{
        option: (styles, { isDisabled, isFocused, isSelected }) => {
          return {
            ...styles,

            backgroundColor: isSelected ? '#f0fff3' : isFocused ? theme.color.brand.inkGrey.grey_6 : undefined,
            color: isSelected ? theme.color.brand.primary : theme.color.text.default,
            cursor: isDisabled ? 'not-allowed' : 'pointer',

            ':active': isSelected || isFocused ? theme.color.brand.primary : undefined,
          };
        },
        control: (styles) => ({
          ...styles,
          borderRadius: theme.dimension.borderRadius.mini,
          fontSize: '0.875rem',
        }),
        menu: (styles) => ({
          ...styles,
          borderRadius: theme.dimension.borderRadius.mini,
          fontSize: '0.875rem',
          zIndex: 1050,
        }),
      }}
      additional={{
        page: 1,
      }}
      defaultAdditional={{
        page: 2,
      }}
    />
  );
}
